import { FC, memo, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { user } from "@lhd/features/users/selector";
import { useFetchTotalUnreadCount, useCheckChatPermission } from "@lhd/hooks";
import { Button } from "antd";
import Draggable from "react-draggable";
import ModalChat from "../CommonLayout/components/ModalChat/ModalChat";
import { Styles } from "./styled";

const GlobalChat: FC = () => {
  const [isOpenChat, setIsOpenChat] = useState<boolean>(false);
  const [initialThread, setInitialThread] = useState<any>(null);
  const [dragging, setDragging] = useState(false);
  const [searchParams] = useSearchParams();
  const auth = useSelector(user);
  const navigate = useNavigate();
  const location = useLocation();
  const { checkPermission } = useCheckChatPermission();
  const refetchDetailedListRef = useRef<(() => void) | null>(null);
  const processingPermissionRef = useRef<string | null>(null);
  const chatButtonRef = useRef<HTMLDivElement>(null);

  const refetchDetailedListCallback = useCallback(() => {
    if (refetchDetailedListRef.current) {
      refetchDetailedListRef.current();
    }
  }, []);

  const { totalUnreadCount } = useFetchTotalUnreadCount({
    isModalOpen: isOpenChat,
    refetchDetailedList: refetchDetailedListCallback,
  });

  useEffect(() => {
    const handleOpenChat = (event: any) => {
      const { thread } = event.detail;
      setInitialThread(thread);
      setIsOpenChat(true);
    };

    window.addEventListener("openChatThread", handleOpenChat);

    return () => {
      window.removeEventListener("openChatThread", handleOpenChat);
    };
  }, []);

  useEffect(() => {
    const threadId = searchParams.get("chat_thread_id");
    if (threadId && auth) {
      if (processingPermissionRef.current === threadId) return;
      processingPermissionRef.current = threadId;

      const verifyAndOpen = async () => {
        try {
          const threadData = await checkPermission(threadId);

          if (threadData) {
            setInitialThread(threadData);
            setIsOpenChat(true);
          }
        } finally {
          const newParams = new URLSearchParams(searchParams.toString());
          newParams.delete("chat_thread_id");
          navigate(
            {
              pathname: location.pathname,
              search: newParams.toString(),
            },
            { replace: true },
          );
        }
      };
      verifyAndOpen();
    } else {
      processingPermissionRef.current = null;
    }
  }, [searchParams, auth, location.pathname, navigate, checkPermission]);

  const [defaultChatPosition] = useState(() => {
    try {
      const saved = localStorage.getItem("chat_bubble_position");
      return saved ? JSON.parse(saved) : { x: 0, y: 0 };
    } catch (e) {
      return { x: 0, y: 0 };
    }
  });

  const handleStop = (_e: any, data: any) => {
    localStorage.setItem(
      "chat_bubble_position",
      JSON.stringify({ x: data.x, y: data.y }),
    );
  };

  const handleCloseChat = useCallback(() => {
    setIsOpenChat(false);
    setInitialThread(null);
  }, []);

  const handleOkChat = useCallback(() => {
    setIsOpenChat(false);
    setInitialThread(null);
  }, []);

  const handleRefetchReady = useCallback((refetch: () => void) => {
    refetchDetailedListRef.current = refetch;
  }, []);

  if (!auth) return null;

  return (
    <Styles>
      <Draggable
        nodeRef={chatButtonRef}
        bounds="body"
        defaultPosition={defaultChatPosition}
        onStart={() => setDragging(false)}
        onDrag={() => setDragging(true)}
        onStop={handleStop}
      >
        <div className="btn-chat" ref={chatButtonRef}>
          <Button
            type="primary"
            aria-label="AI問い合わせ"
            onClick={() => {
              if (!dragging) {
                setIsOpenChat(true);
              }
            }}
          >
            <img src="/images/iconMessage.svg" alt="icon-message" />
            AI問い合わせ
            {totalUnreadCount > 0 && <p>{totalUnreadCount}</p>}
          </Button>
        </div>
      </Draggable>

      <ModalChat
        isOpen={isOpenChat}
        onCancel={handleCloseChat}
        onOk={handleOkChat}
        onRefetchReady={handleRefetchReady}
        initialThread={initialThread}
      />
    </Styles>
  );
};

export default memo(GlobalChat);
